import { AppNode } from "@features/builder/graph/types"
import { getItemColor } from "@features/builder/NodeDrawer/NodeForm/GraphNav/helpers"
import { NodesGroupMapper, nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { FormControlLabel, List, ListItemText, Switch } from "@mui/material"
import ListItem from "@mui/material/ListItem"
import { useStoreApi } from "@xyflow/react"
import { useMemo } from "react"
import { useLocalStorage } from "usehooks-ts"
import { getSortedNodeLabelsById } from "@features/graph/utils/graphNodeUtils"
import { useGoToNode } from "@features/builder/hooks/useGoToNode"

export const GraphNavGroupsWithNodes = (props: { node: AppNode }) => {
  const graphStore = useStoreApi<AppNode>()
  const [showDeepParentGroups, setShowDeepParentGroups] = useLocalStorage(
    `graph-nav-show-deep-group-parents`,
    true,
  )
  const [showDeepSubGroups, setShowDeepSubGroups] = useLocalStorage(
    `graph-nav-show-deep-groups`,
    true,
  )
  const goToNode = useGoToNode()

  const { nodes } = graphStore.getState()
  const mapper = useMemo(() => nodesGroupMapper(nodes), [nodes])

  const parentGroupNodesIds = mapper.groupToNodesIdsMap.get(props.node.id) ?? []
  const sortedParentGroupNodesIds = getSortedNodeLabelsById(nodes, parentGroupNodesIds)

  const subGroupParents = showDeepParentGroups
    ? mapper.childToParentGroupsIdsMapDeep.get(props.node.id) ?? []
    : mapper.childToParentGroupsIdsMap.get(props.node.id) ?? []
  const sortedSubGroupParents = getSortedNodeLabelsById(nodes, subGroupParents)

  const subGroups = showDeepSubGroups
    ? mapper.parentToChildGroupsIdsMapDeep.get(props.node.id) ?? []
    : mapper.parentToChildGroupsIdsMap.get(props.node.id) ?? []
  const sortedSubGroups = getSortedNodeLabelsById(nodes, subGroups)

  return (
    <>
      {sortedParentGroupNodesIds.length > 0 && (
        <NodeDrawerAccordion id="graph-nav-nodes" label="Nodes in current Group:" expand={true}>
          <List sx={{ p: 0 }}>
            {sortedParentGroupNodesIds.map(({ id: nodeId, label }) => (
              <ListItem
                key={nodeId}
                divider
                sx={{
                  backgroundColor: getItemColor(nodeId),
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(225, 225, 225, 0.4)",
                  },
                }}
                onClick={() => goToNode(nodeId)}
              >
                <ListItemText primary={label} />
              </ListItem>
            ))}
          </List>
        </NodeDrawerAccordion>
      )}

      {sortedSubGroupParents.length > 0 && (
        <NodeDrawerAccordion id="graph-nav-sub-group-parents" label="Parent Groups:" expand={true}>
          <FormControlLabel
            control={
              <Switch
                checked={showDeepParentGroups}
                onChange={(e) => setShowDeepParentGroups(e.currentTarget.checked)}
              />
            }
            label="Include all ancestors"
          />
          <List sx={{ p: 0 }}>
            {sortedSubGroupParents.map(({ id: groupId, label }) => (
              <ListItem
                key={groupId}
                divider
                sx={{
                  marginLeft: getIdent(groupId, props.node.id, mapper, "group"),
                  backgroundColor: getItemColor(groupId),
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(225, 225, 225, 0.4)",
                  },
                }}
                onClick={() => goToNode(groupId)}
              >
                <ListItemText primary={label} />
              </ListItem>
            ))}
          </List>
        </NodeDrawerAccordion>
      )}

      {sortedSubGroups.length > 0 && (
        <NodeDrawerAccordion id="graph-nav-sub-groups" label="Sub Groups:" expand={true}>
          <FormControlLabel
            control={
              <Switch
                checked={showDeepSubGroups}
                onChange={(e) => setShowDeepSubGroups(e.currentTarget.checked)}
              />
            }
            label="Include all descendants"
          />
          <List sx={{ p: 0 }}>
            {sortedSubGroups.map(({ id: groupId, label }) => (
              <ListItem
                key={groupId}
                divider
                sx={{
                  marginLeft: getIdent(groupId, props.node.id, mapper, "subGroup"),
                  backgroundColor: getItemColor(groupId),
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(225, 225, 225, 0.4)",
                  },
                }}
                onClick={() => goToNode(groupId)}
              >
                <ListItemText primary={label} />
              </ListItem>
            ))}
          </List>
        </NodeDrawerAccordion>
      )}
    </>
  )
}

function getIdent(
  currNodeId: string,
  topNodeId: string,
  mapper: NodesGroupMapper,
  type: "group" | "subGroup" | "node",
): `${number}em` {
  const currNode = mapper.allMap.get(currNodeId)
  const topNode = mapper.allMap.get(topNodeId)
  const currLevel = currNode?.data.nestingLevel ?? 0
  const topLevel = topNode?.data.nestingLevel ?? 0

  switch (type) {
    case "group":
      return `${topLevel - currLevel - 1}em`
    case "subGroup":
      return `${currLevel - topLevel - 1}em`
    case "node":
      return `${currLevel - topLevel - 1}em`
    default:
      return `0em`
  }
}
